import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href === "/#about" ? "/about" : item.href === "/#contact" ? "/contact" : item.href || "#"} style={{
            animationDelay: `${i * 100}ms`
          }} className={cn("group relative text-sm font-medium text-foreground/80 transition-all duration-300 hover:text-foreground hover:-translate-y-0.5 hover:scale-105", "after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full", "animate-in fade-in slide-in-from-top-1 fill-mode-both")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href === "/#about" ? "/about" : item.href === "/#contact" ? "/contact" : item.href || "#"} className="text-xl font-medium text-foreground/90 hover:text-primary transition-all duration-300 transform hover:translate-x-2" onClick={onClose} data-index={i} style={{
      animationDelay: `${i * 50}ms`
    }}>
          {item.label}
        </Link>)}
      <div className="pt-6 border-t border-border mt-2 flex flex-col gap-3">
        <Button className="w-full transition-transform hover:scale-[1.02] active:scale-[0.98]" size="lg" asChild>
          <Link to="/profissa-pro">Start Exploring the Platform</Link>
        </Button>
        <Button variant="outline" className="w-full transition-transform hover:scale-[1.02] active:scale-[0.98]" size="lg" asChild>
          <Link to="/profissa-pro-overview">Watch the Demo</Link>
        </Button>
        <Button variant="outline" className="w-full transition-transform hover:scale-[1.02] active:scale-[0.98]" size="lg" asChild>
          <Link to="/ProfissaProVisualRedesignPage">Profissa Pro</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 ease-in-out border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-lg border-border shadow-md" : "bg-background")}>
      <div className="container mx-auto px-6 flex items-center justify-between gap-10 !h-[150px] md:px-12">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage#" className="block transition-transform hover:scale-105 active:scale-95">
            <img src="https://certain-zebra.10web.cloud/wp-content/uploads/2026/03/PROFISSA-PRO-LOGO-FUNDO-TRANSPARENTE-Copy-1.png" alt="Profissa Pro" className="!w-[160px] !h-[118px]" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center px-8">
          <MenuProvider menu_id="16" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex flex-shrink-0 items-center gap-3">
          <Button variant="default" className="shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95" asChild>
            <Link to="/profissa-pro">Start Exploring the Platform</Link>
          </Button>
          <Button variant="outline" className="transition-all duration-300 hover:scale-105 active:scale-95" asChild>
            <Link to="/profissa-pro-overview">Watch the Demo</Link>
          </Button>
          <Button variant="outline" className="transition-all duration-300 hover:scale-105 active:scale-95" asChild>
            <Link to="/ProfissaProVisualRedesignPage">Profissa Pro</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" className="hover:bg-accent/50">
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {}
      <div className={cn("fixed inset-0 z-[60] bg-background md:hidden transition-all duration-500 ease-out", isMobileMenuOpen ? "opacity-100 pointer-events-auto translate-x-0" : "opacity-0 pointer-events-none translate-x-full")}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 h-20 border-b border-border">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1565554/img/profissa_pro_logo.png" alt="Profissa Pro" className="h-7" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu" className="hover:bg-accent/50">
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="16">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>
      </div>
    </header>;
}